import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { InactivityWarningModal } from '@/components/InactivityWarningModal';

interface InactivityTimeoutContextType {
  resetTimer: () => void;
  isWarningActive: boolean;
}

const InactivityTimeoutContext = createContext<InactivityTimeoutContextType | undefined>(undefined);

// Time constants (in milliseconds)
const WARNING_TIME = 20 * 60 * 1000; // 20 minutes
const LOGOUT_TIME = 30 * 60 * 1000; // 30 minutes

export function InactivityTimeoutProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, logout } = useAuth();
  const [isWarningActive, setIsWarningActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(10 * 60 * 1000); // 10 minutes remaining after warning
  const lastActivityRef = useRef<number>(Date.now());
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Reset all timers
  const resetTimer = useCallback(() => {
    // Clear existing timers
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    // Reset state
    setIsWarningActive(false);
    setTimeRemaining(10 * 60 * 1000);
    lastActivityRef.current = Date.now();

    // Only set timers if user is authenticated
    if (isAuthenticated) {
      // Set warning timer (20 minutes)
      warningTimerRef.current = setTimeout(() => {
        setIsWarningActive(true);
        setTimeRemaining(10 * 60 * 1000); // 10 minutes remaining

        // Start countdown
        countdownIntervalRef.current = setInterval(() => {
          setTimeRemaining((prev) => {
            if (prev <= 1000) {
              return 0;
            }
            return prev - 1000;
          });
        }, 1000);

        // Set logout timer (10 minutes after warning = 30 minutes total)
        logoutTimerRef.current = setTimeout(() => {
          handleLogout();
        }, 10 * 60 * 1000);
      }, WARNING_TIME);
    }
  }, [isAuthenticated]);

  // Handle logout and page reload
  const handleLogout = useCallback(async () => {
    // Clear all timers first
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    // Logout user (calls backend API and clears localStorage)
    // Use await to ensure backend logout is attempted before reload
    try {
      await logout();
    } catch (error) {
      console.error('Error during timeout logout:', error);
      // Continue with reload even if logout fails
    }

    // Force hard reload of the page - this ensures the app restarts completely
    // Using location.reload() with forceReload flag for a hard reload
    window.location.reload();
  }, [logout]);

  // Throttle activity tracking to avoid excessive resets
  const lastResetRef = useRef<number>(0);
  const THROTTLE_TIME = 5000; // Only reset timer at most once every 5 seconds

  // Track user activity
  const handleActivity = useCallback(() => {
    const now = Date.now();
    const timeSinceLastReset = now - lastResetRef.current;

    // Only reset if enough time has passed since last reset
    // This prevents resetting on every mouse move
    if (timeSinceLastReset >= THROTTLE_TIME) {
      lastActivityRef.current = now;
      lastResetRef.current = now;
      resetTimer();
    }
  }, [resetTimer]);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timers if user is not authenticated
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      setIsWarningActive(false);
      return;
    }

    // Initialize timer on authentication
    resetTimer();

    // Add event listeners for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Also listen to visibility change (when user switches tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        // Reset timer when user comes back to the tab
        resetTimer();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Cleanup event listeners
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      // Clear timers
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [isAuthenticated, handleActivity, resetTimer]);

  // Handle countdown reaching zero
  useEffect(() => {
    if (timeRemaining <= 0 && isWarningActive) {
      console.log('Inactivity timeout reached - logging out and reloading...');
      handleLogout();
    }
  }, [timeRemaining, isWarningActive, handleLogout]);

  return (
    <InactivityTimeoutContext.Provider
      value={{
        resetTimer,
        isWarningActive,
      }}
    >
      {children}
      {isWarningActive && (
        <InactivityWarningModal
          timeRemaining={timeRemaining}
          onStayActive={() => {}} // Not used anymore, but kept for compatibility
        />
      )}
    </InactivityTimeoutContext.Provider>
  );
}

export function useInactivityTimeout() {
  const context = useContext(InactivityTimeoutContext);
  if (context === undefined) {
    throw new Error('useInactivityTimeout must be used within an InactivityTimeoutProvider');
  }
  return context;
}

